import { ClipboardIcon } from "@heroicons/react/20/solid";
import { ICustomDomainSpfCheckResponse } from "shared";
import { toast } from "react-toastify";
import { useCopyToClipboard } from "usehooks-ts";
import { useState } from "react";

import { Button } from "../../../../../common/Atoms/Button";
import { Notification } from "../../../../../common/Atoms/Notification";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { useCheckCustomDomainSpfQuery } from "../../../../services/api/customDomainApi/customDomain";

interface SpfCheckTipProps {
  organisationId: string;
  domain: string;
}

function tipCopy(data: ICustomDomainSpfCheckResponse): {
  title: string;
  body: string;
} | null {
  switch (data.status) {
  case `missing_include`:
    return {
      title: `Tip: add SendGrid to your SPF record`,
      body: `Your domain has an SPF record, but it doesn't authorize SendGrid yet. Adding ${data.requiredInclude} helps your emails reach the inbox. Add it to your existing SPF TXT record — keep it to a single SPF record.`,
    };
  case `no_spf`:
    return {
      title: `Tip: add an SPF record to improve deliverability`,
      body: `Authorizing SendGrid with an SPF record helps your emails reach the inbox. Add a TXT record on ${data.domain ?? `your domain`} with: ${data.exampleRecord}. If you already authorize other mail providers, merge their includes into one SPF record.`,
    };
  default:
    return null;
  }
}

export function SpfCheckTip({
  organisationId,
  domain,
}: SpfCheckTipProps) {
  const { data, isFetching, refetch } = useCheckCustomDomainSpfQuery(organisationId, {
    skip: !organisationId || !domain,
  });
  const [, copy] = useCopyToClipboard();
  const [expanded, setExpanded] = useState(false);

  if (!data) {
    return null;
  }

  const tip = tipCopy(data);

  if (!tip) {
    return null;
  }

  async function handleCopy(text: string) {
    try {
      await copy(text);

      toast.success(`Copied to clipboard`, {
        autoClose: 200,
      });
    }
    catch {
      toast.error(`Failed to copy to clipboard`);
    }
  }

  return (
    <Notification
      type={ `info` }
      title={ tip.title }
      className={ `mt-3` }
      buttons={
        <div className={ `flex items-center gap-3` }>
          {
            expanded && (
              <Button
                onClick={ () => refetch() }
                loading={ isFetching }
                disabled={ isFetching }
                size={ `sm` }
              >
                { `Recheck` }
              </Button>
            )
          }
          <button
            type={ `button` }
            className={ `whitespace-nowrap text-sm font-medium text-blue-700 hover:underline` }
            onClick={ () => setExpanded(prev => !prev) }
            aria-expanded={ expanded }
          >
            { expanded ? `Less` : `More…` }
          </button>
        </div>
      }
    >
      {
        expanded && (
          <div className={ `mt-3 space-y-4` }>
            <Paragraph variant={ `secondary` }>
              { tip.body }
            </Paragraph>
            <div className={ `flex flex-wrap items-center gap-3` }>
              <code className={ `rounded bg-white/60 px-2 py-1 text-xs` }>
                { data.requiredInclude }
              </code>
              <button
                type={ `button` }
                aria-label={ `Copy required SPF include` }
                className={ `inline-flex` }
                onClick={ () => handleCopy(data.requiredInclude) }
              >
                <ClipboardIcon className={ `h-5 w-5 text-paidniceGreen-500` } />
              </button>
              <code className={ `rounded bg-white/60 px-2 py-1 text-xs` }>
                { data.exampleRecord }
              </code>
              <button
                type={ `button` }
                aria-label={ `Copy example SPF record` }
                className={ `inline-flex` }
                onClick={ () => handleCopy(data.exampleRecord) }
              >
                <ClipboardIcon className={ `h-5 w-5 text-paidniceGreen-500` } />
              </button>
            </div>
            {
              data.currentRecord && (
                <Paragraph
                  variant={ `help` }
                  className={ `break-all` }
                >
                  { `Current SPF: ${data.currentRecord}` }
                </Paragraph>
              )
            }
          </div>
        )
      }
    </Notification>
  );
}
